import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, TextField, InputAdornment } from "@mui/material";
import { useSnackbar } from "notistack";
import {
  listVisits,
  createVisit,
  type VisitListItem,
} from "../../lib/brain-api";
import { brainTokens } from "../../design/brainTheme";
import { SectionCard, StatusPill, Segmented, Icon } from "../../mui";
import { Explain } from "../../agent/Explain";
import { relDate } from "../../mui/format";
// ponytail: reuses the complete legacy VisitDetail (all 8 report sections + anchor
// rail) rather than re-porting 600 lines to MUI mid-feature. kt-* CSS is still
// global; the proper MUI port belongs to the 2026-07-20 MUI migration plan.
import { VisitDetail } from "../../components/visits/VisitDetail";
import { ImportTranscriptModal } from "../../components/visits/ImportTranscriptModal";

const c = brainTokens.color;

const STAGE: Record<string, { label: string; pct: number; tone: "success" | "teal" }> = {
  open: { label: "OPEN", pct: 20, tone: "teal" },
  ready: { label: "READY", pct: 65, tone: "teal" },
  audited: { label: "AUDITED", pct: 100, tone: "success" },
};

type FilterKey = "all" | "open" | "ready" | "audited";
type SortKey = "newest" | "oldest";

export function CustomerVisitsTabMui({ customerId }: { customerId: string }) {
  const { enqueueSnackbar } = useSnackbar();
  const [visits, setVisits] = useState<VisitListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<FilterKey>("all");
  const [sort, setSort] = useState<SortKey>("newest");
  const [importOpen, setImportOpen] = useState(false);
  const [droppedFile, setDroppedFile] = useState<File | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setVisits(await listVisits(customerId));
    } catch {
      enqueueSnackbar("Failed to load visits.", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [customerId, enqueueSnackbar]);
  useEffect(() => {
    void load();
  }, [load]);

  const newVisit = async () => {
    try {
      const v = await createVisit({ customerId });
      await load();
      setSelected(v.id);
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Create failed.", { variant: "error" });
    }
  };

  const needle = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    let rows = visits.filter((v) => {
      if (filter !== "all" && v.status !== filter) return false;
      if (needle && ![v.title, v.id, v.startsOn].join(" ").toLowerCase().includes(needle)) return false;
      return true;
    });
    rows = [...rows].sort((a, b) => (sort === "newest" ? b.startsOn.localeCompare(a.startsOn) : a.startsOn.localeCompare(b.startsOn)));
    return rows;
  }, [visits, filter, needle, sort]);

  const byYear = useMemo(() => {
    const m = new Map<string, VisitListItem[]>();
    for (const v of filtered) {
      const y = v.startsOn.slice(0, 4) || "—";
      if (!m.has(y)) m.set(y, []);
      m.get(y)!.push(v);
    }
    return [...m.entries()].sort((a, b) => (sort === "newest" ? b[0].localeCompare(a[0]) : a[0].localeCompare(b[0])));
  }, [filtered, sort]);

  const totalOpen = visits.reduce((s, v) => s + v.openItemCount, 0);
  const latest = visits.reduce<string>((mx, v) => (v.startsOn > mx ? v.startsOn : mx), "");

  if (selected) {
    return <VisitDetail customerId={customerId} visitId={selected} onBack={() => { setSelected(null); void load(); }} />;
  }

  return (
    <Box
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        const f = e.dataTransfer.files?.[0];
        if (f) { setDroppedFile(f); setImportOpen(true); }
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: "22px", gap: 2 }}>
        <Box>
          <Box sx={{ fontSize: 17, fontWeight: 600, mb: "4px" }}>Site visits</Box>
          <Box sx={{ fontSize: 13, color: c.muted2 }}>
            {visits.length} visit{visits.length === 1 ? "" : "s"} · {totalOpen} open items across all reports
            {latest ? ` · latest ${latest}` : ""}
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" onClick={() => { setDroppedFile(null); setImportOpen(true); }}>
            Add transcript
          </Button>
          <Button variant="contained" onClick={() => void newVisit()} startIcon={<Icon name="arrow-right" size={15} stroke={2} />}>
            New site visit
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: "24px", flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flex: 1, minWidth: 280 }}>
          <TextField
            size="small"
            placeholder="Search title, date, id…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ flex: 1, maxWidth: 340 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Box sx={{ color: c.faint2, display: "flex" }}>
                    <Icon name="search" size={16} stroke={1.8} />
                  </Box>
                </InputAdornment>
              ),
            }}
          />
          <Segmented
            value={filter}
            onChange={setFilter}
            options={[
              { key: "all", label: "All" },
              { key: "open", label: "Open" },
              { key: "ready", label: "Ready" },
              { key: "audited", label: "Audited" },
            ]}
          />
        </Box>
        <Segmented
          value={sort}
          onChange={setSort}
          options={[
            { key: "newest", label: "Newest first" },
            { key: "oldest", label: "Oldest first" },
          ]}
        />
      </Box>

      {loading ? (
        <Box sx={{ color: c.muted2, fontSize: 14, py: 4 }}>Loading visits…</Box>
      ) : byYear.length === 0 ? (
        <SectionCard>
          <Box sx={{ fontSize: 13.5, color: c.muted2, textAlign: "center", py: 3 }}>No visits match.</Box>
        </SectionCard>
      ) : (
        byYear.map(([year, rows]) => {
          const yearOpen = rows.reduce((s, v) => s + v.openItemCount, 0);
          return (
            <Box key={year} sx={{ mb: "28px" }}>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: "10px", mb: "12px" }}>
                <Box sx={{ fontSize: 17, fontWeight: 700 }}>{year}</Box>
                <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 12, color: c.faint2 }}>
                  {rows.length} visit{rows.length === 1 ? "" : "s"} · {yearOpen} open
                </Box>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {rows.map((v) => {
                  const stage = STAGE[v.status] ?? STAGE.open;
                  return (
                    <Box key={v.id} sx={{ background: "#fff", border: `1px solid ${c.border}`, borderRadius: "8px", p: "18px 20px", "&:hover": { borderColor: "#c7d6d6" } }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
                        <Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: "6px", flexWrap: "wrap" }}>
                            <Box sx={{ fontSize: 15, fontWeight: 600 }}>{v.title}</Box>
                            <StatusPill
                              tone={stage.tone}
                              label={`${stage.label} ${stage.pct}%`}
                              sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.04em", borderRadius: "4px", padding: "2px 7px" }}
                            />
                            <Box sx={{ display: "inline-flex", alignItems: "center", gap: "5px", fontSize: 12.5, color: c.muted2 }}>
                              <Icon name={v.type === "remote" ? "activity" : "map-pin"} size={12} stroke={1.8} />
                              {v.type === "remote" ? "Remote" : "On-site"}
                            </Box>
                          </Box>
                          <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 12.5, color: c.faint }}>visit:{v.id}</Box>
                        </Box>
                        <Box sx={{ textAlign: "right", flex: "none" }}>
                          <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 12.5, color: c.muted }}>{v.startsOn}</Box>
                          <Box sx={{ fontSize: 12, color: c.faint2, mt: "2px" }}>{relDate(Date.parse(v.startsOn))}</Box>
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: "16px", pt: "14px", borderTop: `1px solid ${c.borderLight2}` }}>
                        <Box sx={{ display: "flex", gap: "20px", fontSize: 12.5, color: c.muted2 }}>
                          <Explain
                            snapshot={{ label: "Open items", value: v.openItemCount, visit: v.id }}
                            prompt={`Summarise the open items from visit ${v.id} for ${customerId} and how stale they are. Cite [ref:visit:${customerId}/${v.id}].`}
                          >
                            <span>
                              <Box component="b" sx={{ color: v.openItemCount ? c.warnText2 : c.ink4, fontWeight: 600 }}>{v.openItemCount}</Box> open items
                            </span>
                          </Explain>
                          <span>
                            <Box component="b" sx={{ fontWeight: 600, color: c.ink4 }}>{v.meetingCount}</Box> meetings
                          </span>
                        </Box>
                        <Box component="button" onClick={() => setSelected(v.id)} sx={{ background: "none", border: "none", fontFamily: brainTokens.font.sans, fontSize: 13, fontWeight: 600, color: c.tealInk2, cursor: "pointer", "&:hover": { color: c.tealHover } }}>
                          Open report →
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        })
      )}
      <ImportTranscriptModal
        customerId={customerId}
        open={importOpen}
        initialFile={droppedFile}
        onClose={() => setImportOpen(false)}
        onDone={(vid) => { void load(); setSelected(vid); }}
      />
    </Box>
  );
}
